import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useModal } from '../../Providers/ModalContext';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1440px',
};

const overlayIn = keyframes`
    0% {
        opacity: 0;
        visibility: hidden;
        translate: 0 0;
    }
    100% {
        opacity: 1;
        visibility: visible;
    }
`;

const modalIn = keyframes`
    0% {
        opacity: 0;
        visibility: hidden;
    }
    100% {
        opacity: 1;
        visibility: visible;
    }
`;

const Overlay = styled.div`
  position: fixed;
  opacity: 0;
  visibility: hidden;
  transition: 0.5s;
  z-index: 1001;
  overflow-y: auto;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ $bgColor = 'rgba(0, 0, 0, 0)' }) => $bgColor};
  backdrop-filter: ${({ $filter = 'none' }) => $filter};
  background-image: url(${({ $bgImg = '' }) => $bgImg});
  background-repeat: no-repeat;
  background-size: cover;
  background-attachment: local;
  background-position: ${({ $bgPosition = 'top center' }) => $bgPosition};

  &::before {
    content: ${({ $borderOut = 'none' }) => $borderOut};
    position: absolute;
    inset: 0;
    z-index: 1;
    padding: 1.5px;
    background: linear-gradient(
      30deg,
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 0)
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  &.open {
    display: block;
    visibility: visible;
    opacity: 1;
    animation: ${overlayIn} 1s both;

    .modal {
      opacity: 1;
      visibility: visible;
      animation: ${modalIn} 1s;
    }
  }
`;

const ModalCotent = styled.div`
  position: fixed;
  opacity: 0;
  visibility: hidden;
  transition: 0.5s;

  z-index: 1000;
  width: ${({ $contentWidth = '100%' }) => $contentWidth};
  max-width: ${({ $contentMaxWidth = 'none' }) => $contentMaxWidth};
  height: ${({ $contentHeight = '100%' }) => $contentHeight};
  max-height: ${({ $contentMaxHeight = 'none' }) => $contentMaxHeight};
  top: 50%;
  left: 50%;

  background: ${({ $bgContent = 'transparent' }) => $bgContent};
  padding: ${({ $padding = '40px 50px' }) => $padding};
  translate: -50% -50%;

  background-image: url(${({ $bgImgContent = '' }) => $bgImgContent});
  background-repeat: no-repeat;
  background-size: contain;
  background-attachment: local;
  background-position: ${({ $bgPositionContent = 'top center' }) =>
    $bgPositionContent};

  overflow: ${({ $overFlow = 'auto' }) => $overFlow};

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${({ $mobPadding = '20px' }) => $mobPadding};
    max-width: ${({ $mobContWidth = 'none' }) => $mobContWidth};
    height: ${({ $mobContHeight = '100%' }) => $mobContHeight};
    background-image: url(${({ $mobBgContImg = '' }) => $mobBgContImg});
  }

  [class*='__CloseContainer'] {
    z-index: 10;
    top: 7.5%;
    right: 2%;

    @media (max-width: ${breakpoints.tablet}) {
      top: 4.5%;
    }

    @media (max-width: ${breakpoints.mobile}) {
      top: 3.5%;
      right: 1.5%;
    }
  }

  [class*='__LogoContent'] {
    @media (max-width: ${breakpoints.tablet}) {
      max-width: 186px;
      padding: 0;
    }

    @media (max-width: ${breakpoints.mobile}) {
      max-width: 104px;
      padding: 0;
    }

    &::after {
      @media (max-width: ${breakpoints.tablet}) {
        width: 6px;
        height: 83px;
        right: -15%;
      }

      @media (max-width: ${breakpoints.mobile}) {
        width: 3px;
        height: 70px;
        right: -12%;
      }
    }
  }

  [class*='__TitleContent'] {
    @media (max-width: ${breakpoints.tablet}) {
      padding-right: 10%;
      gap: 3rem;
    }

    @media (max-width: ${breakpoints.mobile}) {
      padding-right: 10%;
      gap: 1.5rem;
    }

    [class*='__Paragraph'] {
      @media (max-width: ${breakpoints.tablet}) {
        font-size: 2.8rem;
      }

      @media (max-width: ${breakpoints.mobile}) {
        font-size: 1.5rem;
      }
    }
  }

  [class*='__ModalContent'] {
    @media (max-width: ${breakpoints.mobile}) {
      padding: 25px 10px;
    }
  }
`;

const Modal = ({
  id,
  bgColor,
  bgImg,
  bgPosition,
  filter,
  borderOut,
  padding,
  bgContent,
  contentWidth,
  contentHeight,
  contentMaxWidth,
  contentMaxHeight,
  bgImgContent,
  bgPositionContent,
  overFlow,
  children,
  mobPadding,
  mobContWidth,
  mobContHeight,
  mobBgContImg,
}) => {
  const { modalStack, closeModal } = useModal();
  const isOpen = modalStack.includes(id);
  const zIndex = 1000 + modalStack.indexOf(id);

  if (!isOpen) return null;

  return (
    <>
      <Overlay
        id={id}
        style={{ zIndex }}
        className={`${isOpen ? 'open' : ''}`}
        onClick={closeModal}
        $bgColor={bgColor}
        $filter={filter}
        $borderOut={borderOut}
        $bgImg={bgImg}
        $bgPosition={bgPosition}
      >
        <ModalCotent
          className="modal"
          onClick={(e) => e.stopPropagation()}
          $padding={padding}
          $bgContent={bgContent}
          $contentWidth={contentWidth}
          $contentHeight={contentHeight}
          $contentMaxHeight={contentMaxHeight}
          $contentMaxWidth={contentMaxWidth}
          $bgImgContent={bgImgContent}
          $bgPositionContent={bgPositionContent}
          $overFlow={overFlow}
          $mobPadding={mobPadding}
          $mobContWidth={mobContWidth}
          $mobContHeight={mobContHeight}
          $mobBgContImg={mobBgContImg}
        >
          {children}
        </ModalCotent>
      </Overlay>
    </>
  );
};

export default Modal;
